import { NavLink, useParams } from "react-router-dom";
import { PipelineRail } from "../../components/PipelineRail";
import { APP_TAB_PATH_TO_DOSSIER_PERMISSION, canSeeDossierMilestonesTab } from "../../config/applicationHubPermissions";
import { useAuth } from "../../context/AuthContext";
import { useAppState } from "../../context/AppStateContext";
import { APP_TAB_ROUTES } from "../appTabs";
import { areMilestonesAndBillsUnavailable } from "../milestoneBillsGate";
import { getFriendlyNextStep } from "../nextStep";

export function OverviewTab() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const { getById, getSubcontractorMaster, milestoneTemplates, subcontractorMasters } = useAppState();
  const app = id ? getById(id) : undefined;
  if (!app) return null;
  const base = `/applications/${app.id}`;
  const linkedSubName = app.subcontractor.subcontractorMasterId
    ? getSubcontractorMaster(app.subcontractor.subcontractorMasterId)?.firmName
    : undefined;
  const v = app.validations;
  const next = getFriendlyNextStep(app);
  const milestonesBillsLocked = areMilestonesAndBillsUnavailable(app);
  const milestonesTabAllowed = canSeeDossierMilestonesTab(can);
  const fullApplicantAccess = can("applications");
  const tabReachable = (path: (typeof APP_TAB_ROUTES)[number]["path"]) =>
    fullApplicantAccess || can(APP_TAB_PATH_TO_DOSSIER_PERMISSION[path]);

  return (
    <>
      <div className="help-banner help-banner--welcome">
        <strong>How this page works</strong>
        <p>
          This is the <strong>summary</strong> for one beneficiary. The coloured bar shows how far the case has moved.
          Below you can open any section from the cards, or use the <strong>green links on the left</strong> — they stay
          visible on every screen.
        </p>
      </div>

      <div className="help-banner help-banner--next">
        <span className="help-banner__badge">Suggested next step</span>
        <h3 className="help-banner__title">{next.title}</h3>
        <p className="help-banner__body">{next.body}</p>
        <NavLink to={next.to} className="btn btn--primary btn--tg help-banner__btn">
          {next.cta}
        </NavLink>
      </div>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Progress for this case</h2>
        <p className="section-lead">
          <strong>Green</strong> steps are finished; <strong>blue</strong> is the step that needs you now. Lines between
          steps turn green as each stage is completed.
        </p>
        <PipelineRail application={app} masters={subcontractorMasters} />
      </div>

      {app.holdReasons.length > 0 ? (
        <div className="alert alert--hold">
          <strong>Action needed — file is on hold.</strong>
          <p className="alert__lead">Please fix the issues below, then update the checklist on the Validation tab.</p>
          <ul>
            {app.holdReasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {!v.aadhaarSeedingConfirmed && app.holdReasons.length === 0 ? (
        <div className="alert alert--info">
          <strong>Banking is locked until seeding is confirmed.</strong> Open{" "}
          <NavLink to={`${base}/validation`}>Verification &amp; Aadhaar seeding</NavLink> and complete the steps there
          first.
        </div>
      ) : null}

      <h2 className="section-heading section-heading--inset">Key details at a glance</h2>
      <p className="section-sub section-sub--tight">Tap a card link to see the full section.</p>

      <div className="summary-grid">
        <div className="summary-card">
          <h3 className="summary-card__title">Beneficiary</h3>
          <dl className="summary-dl">
            <dt>Name</dt>
            <dd>{app.mobile.beneficiaryName}</dd>
            <dt>Father / husband</dt>
            <dd>{app.mobile.fatherOrHusbandName}</dd>
            <dt>Aadhaar</dt>
            <dd className="mono">{app.mobile.aadhaarNumber}</dd>
          </dl>
          <NavLink to={`${base}/beneficiary`} className="summary-card__link">
            See all beneficiary fields &amp; documents
          </NavLink>
        </div>
        <div className="summary-card">
          <h3 className="summary-card__title">Verification</h3>
          <dl className="summary-dl">
            <dt>Name match</dt>
            <dd>{v.nameMatchAcrossSources === true ? "OK" : v.nameMatchAcrossSources === false ? "Issue" : "Not set"}</dd>
            <dt>Aadhaar checked</dt>
            <dd>{v.aadhaarVerified === true ? "OK" : v.aadhaarVerified === false ? "Issue" : "Not set"}</dd>
            <dt>Seeding</dt>
            <dd>{v.aadhaarSeedingConfirmed ? "Done" : "Not yet"}</dd>
          </dl>
          <NavLink to={`${base}/validation`} className="summary-card__link">
            Open verification &amp; seeding
          </NavLink>
        </div>
        <div className="summary-card">
          <h3 className="summary-card__title">ICICI &amp; MOU</h3>
          <dl className="summary-dl">
            <dt>Account entered</dt>
            <dd>{app.ho.iciciAccountNumber ? "Yes" : "No"}</dd>
            <dt>MOU file</dt>
            <dd>
              {app.ho.mouFileName ||
              app.ho.mouFileStored ||
              (app.ho.mouAttachments?.length ?? 0) > 0
                ? "Attached"
                : "Not yet"}
            </dd>
          </dl>
          <NavLink to={`${base}/banking`} className="summary-card__link">
            Open ICICI &amp; MOU
          </NavLink>
        </div>
        <div className="summary-card">
          <h3 className="summary-card__title">Milestones</h3>
          <dl className="summary-dl">
            <dt>Open phases</dt>
            <dd>{app.milestoneProgress.filter((p) => !p.milestoneCompletedAt).length}</dd>
            <dt>Items pending</dt>
            <dd>
              {app.milestoneProgress.reduce((n, p) => {
                const t = milestoneTemplates.find((x) => x.id === p.templateId);
                if (!t) return n;
                return (
                  n +
                  t.items.filter((it) => {
                    const ip = p.itemProgress.find((i) => i.templateItemId === it.id);
                    return !ip?.completedAt;
                  }).length
                );
              }, 0)}
            </dd>
            <dt>Phases complete</dt>
            <dd>{app.milestoneProgress.filter((p) => p.milestoneCompletedAt).length}</dd>
          </dl>
          {milestonesBillsLocked ? (
            <span className="summary-card__link summary-card__link--muted" aria-disabled="true">
              Milestones — coming soon
            </span>
          ) : milestonesTabAllowed && tabReachable("milestones") ? (
            <NavLink to={`${base}/milestones`} className="summary-card__link">
              Complete master checklist for this file
            </NavLink>
          ) : (
            <span className="summary-card__link summary-card__link--muted" aria-disabled="true">
              Milestones — permission required (milestone master + dossier milestones tab or full applicant access)
            </span>
          )}
        </div>
        <div className="summary-card">
          <h3 className="summary-card__title">Bills &amp; subcontractor</h3>
          <dl className="summary-dl">
            <dt>Work bills</dt>
            <dd>{app.workDoneBills.length}</dd>
            <dt>Awaiting site confirm</dt>
            <dd>{app.workDoneBills.filter((b) => !b.seedingReVerified).length}</dd>
            <dt>Subcontractor</dt>
            <dd>
              {app.status === "subcontractor_linked"
                ? linkedSubName ?? "Linked (master)"
                : "Not linked"}
            </dd>
          </dl>
          <div className="summary-card__links">
            {milestonesBillsLocked ? (
              <span className="summary-card__link summary-card__link--muted" aria-disabled="true">
                Bills — coming soon
              </span>
            ) : tabReachable("bills") ? (
              <NavLink to={`${base}/bills`} className="summary-card__link">
                Bills
              </NavLink>
            ) : (
              <span className="summary-card__link summary-card__link--muted" aria-disabled="true">
                Bills — no access
              </span>
            )}
            {tabReachable("subcontractor") ? (
              <NavLink to={`${base}/subcontractor`} className="summary-card__link">
                Subcontractor
              </NavLink>
            ) : (
              <span className="summary-card__link summary-card__link--muted" aria-disabled="true">
                Subcontractor — no access
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="card card--gov">
        <h2 className="card__title card__title--gov">All sections (same as left menu)</h2>
        <p className="section-lead section-lead--tight">Choose a row to go straight to that part of the file.</p>
        <ul className="section-index">
          {APP_TAB_ROUTES.filter((t) => t.path !== "overview").map((t) => {
            const lockedBySubStage =
              milestonesBillsLocked && (t.path === "milestones" || t.path === "bills");
            const lockedMilestonesNoMaster = t.path === "milestones" && !milestonesTabAllowed;
            const lockedNoTabScope = !fullApplicantAccess && !can(APP_TAB_PATH_TO_DOSSIER_PERMISSION[t.path]);
            const lockedHere = lockedBySubStage || lockedMilestonesNoMaster || lockedNoTabScope;
            return (
              <li key={t.path}>
                {lockedHere ? (
                  <div className="section-index__row section-index__row--muted">
                    <span className="section-index__en">
                      {t.label}
                      <span className="section-index__badge">
                        {lockedNoTabScope && !lockedBySubStage
                          ? "No access"
                          : lockedMilestonesNoMaster && !lockedBySubStage
                            ? "No access"
                            : "Coming soon"}
                      </span>
                    </span>
                    <span className="section-index__desc">
                      {lockedNoTabScope && !lockedBySubStage
                        ? "Your role does not include this dossier section. Ask an administrator to grant the matching tab permission."
                        : lockedMilestonesNoMaster && !lockedBySubStage
                          ? "Milestone master (templates) plus dossier milestones access is required for milestone checklists."
                          : "Not available in this console yet (before or after subcontractor). Your office will enable this when ready."}
                    </span>
                  </div>
                ) : (
                  <NavLink to={`${base}/${t.path}`}>
                    <span className="section-index__en">{t.label}</span>
                    <span className="section-index__desc">{t.userHint}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
